import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical,
  Trash2,
  Plus,
  ArrowUp,
  ArrowDown,
  ArrowLeftRight,
  Save,
  RotateCcw,
  Lock,
  X,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const EXTRA_LABOR = "Extra Labor";

const clone = (obj) => JSON.parse(JSON.stringify(obj || { left: [], right: [] }));

// ---- Linha de item (arrastável) ----
function SortableItemRow({ id, item, onChange, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2 py-1.5"
      data-testid="designer-item-row"
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-700 touch-none"
        {...attributes}
        {...listeners}
        data-testid="designer-item-drag-handle"
        aria-label="Arrastar item"
      >
        <GripVertical size={16} />
      </button>
      <Input
        value={item.description}
        onChange={(e) => onChange("description", e.target.value)}
        placeholder="Nome do item"
        className="h-8 text-sm flex-1"
        data-testid="designer-item-desc-input"
      />
      <div className="flex items-center gap-1">
        <span className="text-slate-400 text-sm">$</span>
        <Input
          type="number"
          step="1"
          value={item.unit_price}
          onChange={(e) => onChange("unit_price", e.target.value)}
          className="h-8 text-sm w-24"
          data-testid="designer-item-price-input"
        />
      </div>
      <button
        type="button"
        onClick={onDelete}
        className="text-slate-400 hover:text-red-600 p-1"
        data-testid="designer-item-delete"
        aria-label="Remover item"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
}

// ---- Seção ----
function SectionCard({
  col,
  sIdx,
  section,
  isFirst,
  isLast,
  onTitle,
  onDeleteSection,
  onMoveSection,
  onToggleColumn,
  onAddItem,
  onItemChange,
  onItemDelete,
  onReorderItems,
}) {
  const special = section.special === "extra_labor" || section.title === EXTRA_LABOR;
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const itemIds = (section.items || []).map((_, i) => `${col}-${sIdx}-item-${i}`);

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 mb-3" data-testid="designer-section-card">
      <div className="flex items-center gap-2 mb-2">
        {special ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-1 rounded-md">
            <Lock size={12} /> {section.title}
          </span>
        ) : (
          <Input
            value={section.title}
            onChange={(e) => onTitle(e.target.value)}
            placeholder="Título da seção"
            className="h-9 font-semibold text-sm flex-1 bg-white"
            data-testid="designer-section-title-input"
          />
        )}
        <div className="flex items-center gap-0.5 ml-auto">
          <button type="button" disabled={isFirst} onClick={() => onMoveSection(-1)}
            className="p-1 text-slate-400 hover:text-slate-800 disabled:opacity-30" data-testid="designer-section-up" aria-label="Subir seção">
            <ArrowUp size={16} />
          </button>
          <button type="button" disabled={isLast} onClick={() => onMoveSection(1)}
            className="p-1 text-slate-400 hover:text-slate-800 disabled:opacity-30" data-testid="designer-section-down" aria-label="Descer seção">
            <ArrowDown size={16} />
          </button>
          {!special && (
            <>
              <button type="button" onClick={onToggleColumn}
                className="p-1 text-slate-400 hover:text-indigo-700" data-testid="designer-section-move-col" aria-label="Trocar de coluna" title="Mover para a outra coluna">
                <ArrowLeftRight size={16} />
              </button>
              <button type="button" onClick={onDeleteSection}
                className="p-1 text-slate-400 hover:text-red-600" data-testid="designer-section-delete" aria-label="Remover seção">
                <Trash2 size={16} />
              </button>
            </>
          )}
        </div>
      </div>

      {special ? (
        <p className="text-xs text-slate-500 px-1">
          Seção especial: linhas de Extra Labor são preenchidas por bill (não editável aqui).
        </p>
      ) : (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(e) => {
              const { active, over } = e;
              if (!over || active.id === over.id) return;
              const oldI = itemIds.indexOf(active.id);
              const newI = itemIds.indexOf(over.id);
              if (oldI !== -1 && newI !== -1) onReorderItems(oldI, newI);
            }}
          >
            <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-1.5">
                {(section.items || []).map((it, iIdx) => (
                  <SortableItemRow
                    key={itemIds[iIdx]}
                    id={itemIds[iIdx]}
                    item={it}
                    onChange={(field, value) => onItemChange(iIdx, field, value)}
                    onDelete={() => onItemDelete(iIdx)}
                  />
                ))}
                {(section.items || []).length === 0 && (
                  <p className="text-xs text-slate-400 px-1 py-2">Sem itens. Adicione abaixo.</p>
                )}
              </div>
            </SortableContext>
          </DndContext>
          <button
            type="button"
            onClick={onAddItem}
            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-indigo-700 hover:text-indigo-900"
            data-testid="designer-add-item"
          >
            <Plus size={14} /> Adicionar item
          </button>
        </>
      )}
    </div>
  );
}

function ColumnEditor({ col, sections, label, onChange, onCrossMove }) {
  const update = (fn) => {
    const next = clone({ left: [], right: [] });
    next[col] = sections;
    fn(next[col]);
    onChange(col, next[col]);
  };

  const setTitle = (sIdx, title) => update((secs) => { secs[sIdx].title = title; });
  const deleteSection = (sIdx) => update((secs) => { secs.splice(sIdx, 1); });
  const moveSection = (sIdx, dir) => update((secs) => {
    const j = sIdx + dir;
    if (j < 0 || j >= secs.length) return;
    [secs[sIdx], secs[j]] = [secs[j], secs[sIdx]];
  });
  const addSection = () => update((secs) => {
    // inserir antes de qualquer seção especial (Extra Labor fica no fim)
    const insertAt = secs.findIndex((s) => s.special === "extra_labor");
    const node = { title: "Nova Seção", items: [] };
    if (insertAt === -1) secs.push(node); else secs.splice(insertAt, 0, node);
  });
  const addItem = (sIdx) => update((secs) => {
    secs[sIdx].items = secs[sIdx].items || [];
    secs[sIdx].items.push({ description: "Novo item", unit_price: 0, qty_field: null, total_field: null });
  });
  const itemChange = (sIdx, iIdx, field, value) => update((secs) => {
    secs[sIdx].items[iIdx] = {
      ...secs[sIdx].items[iIdx],
      [field]: field === "unit_price" ? value : value,
    };
  });
  const itemDelete = (sIdx, iIdx) => update((secs) => { secs[sIdx].items.splice(iIdx, 1); });
  const reorderItems = (sIdx, oldI, newI) => update((secs) => {
    secs[sIdx].items = arrayMove(secs[sIdx].items, oldI, newI);
  });

  return (
    <div className="flex-1 min-w-0" data-testid={`designer-column-${col}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">{label}</h3>
        <Button size="sm" variant="outline" onClick={addSection} className="h-7 text-xs" data-testid={`designer-add-section-${col}`}>
          <Plus size={14} className="mr-1" /> Seção
        </Button>
      </div>
      {sections.map((section, sIdx) => (
        <SectionCard
          key={`${col}-${sIdx}`}
          col={col}
          sIdx={sIdx}
          section={section}
          isFirst={sIdx === 0}
          isLast={sIdx === sections.length - 1}
          onTitle={(t) => setTitle(sIdx, t)}
          onDeleteSection={() => deleteSection(sIdx)}
          onMoveSection={(dir) => moveSection(sIdx, dir)}
          onToggleColumn={() => onCrossMove(col, sIdx)}
          onAddItem={() => addItem(sIdx)}
          onItemChange={(iIdx, field, value) => itemChange(sIdx, iIdx, field, value)}
          onItemDelete={(iIdx) => itemDelete(sIdx, iIdx)}
          onReorderItems={(oldI, newI) => reorderItems(sIdx, oldI, newI)}
        />
      ))}
    </div>
  );
}

export default function LaborBillDesigner({ open, onClose, apiBase, initialTemplate, onSaved }) {
  const [tpl, setTpl] = useState(clone(initialTemplate));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setTpl(clone(initialTemplate));
  }, [open, initialTemplate]);

  if (!open) return null;

  const setColumn = (col, sections) => setTpl((prev) => ({ ...prev, [col]: sections }));

  // Mover uma seção de uma coluna para a outra
  const crossMove = (fromCol, sIdx) => {
    setTpl((prev) => {
      const next = clone(prev);
      const [sec] = next[fromCol].splice(sIdx, 1);
      const toCol = fromCol === "left" ? "right" : "left";
      const insertAt = next[toCol].findIndex((s) => s.special === "extra_labor");
      if (insertAt === -1) next[toCol].push(sec); else next[toCol].splice(insertAt, 0, sec);
      return next;
    });
  };

  const sanitizePrices = (t) => {
    const fix = (secs) => (secs || []).map((s) => ({
      ...s,
      items: (s.items || []).map((it) => ({
        ...it,
        description: (it.description || "").trim(),
        unit_price: Number(it.unit_price) || 0,
      })).filter((it) => it.description),
    }));
    return { left: fix(t.left), right: fix(t.right) };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = sanitizePrices(tpl);
      const res = await axios.put(`${apiBase}/labor-bill-template`, payload);
      const saved = res.data?.template || payload;
      toast.success("Template do Labor Bill salvo!");
      onSaved && onSaved(saved);
      onClose && onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erro ao salvar o template";
      toast.error(typeof msg === "string" ? msg : "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Restaurar o catálogo padrão? Isso descarta as personalizações.")) return;
    setSaving(true);
    try {
      const res = await axios.post(`${apiBase}/labor-bill-template/reset`);
      const def = res.data?.template;
      if (def) { setTpl(clone(def)); onSaved && onSaved(def); }
      toast.success("Catálogo padrão restaurado");
    } catch (e) {
      toast.error("Erro ao restaurar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-2 sm:p-4" data-testid="labor-designer-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-indigo-600 to-indigo-500">
          <div>
            <h2 className="text-white text-lg font-bold">Designer do Labor Bill</h2>
            <p className="text-indigo-100 text-xs">Renomeie, reordene (arraste), mude preços e crie seções/itens</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1" data-testid="designer-close" aria-label="Fechar">
            <X size={22} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 bg-slate-100">
          <div className="flex flex-col lg:flex-row gap-4">
            <ColumnEditor col="left" label="Coluna Esquerda" sections={tpl.left || []} onChange={setColumn} onCrossMove={crossMove} />
            <ColumnEditor col="right" label="Coluna Direita" sections={tpl.right || []} onChange={setColumn} onCrossMove={crossMove} />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-200 bg-white">
          <Button variant="ghost" onClick={handleReset} disabled={saving} className="text-slate-600" data-testid="designer-reset">
            <RotateCcw size={16} className="mr-1" /> Restaurar padrão
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving} data-testid="designer-cancel">Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="designer-save">
              <Save size={16} className="mr-1" /> {saving ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
